import { ILocalization } from '../localization/definitions/types';
import { AdminApprovalOutcomeEnum } from '../store/projectVersionHistoryChangeType';
import {
    ProjectModel,
    User,
    ProjectIndexMap,
    IProjectRegistrationModel,
    ProjectBrowserModel,
    ProjectTimeline,
} from '../store/types';
import {
    DateFormat,
    formatDateWithShortTime,
    isDateBeforeCheckDate,
} from 'susy-common/date-utils';
import {
    DeadlineStatus,
    InvitationStatus,
    ProjectSecondSupervisorStatus,
    ProjectStatus,
} from './utils';

const NO_DATA = '-';
export const ARCHIVE_DURATION_IN_DAYS = 30;

//rename this method, we need project that are approved or more down the lifecycle, not just status approve
export const getApprovedProjects = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) => currentStatus === ProjectStatus.approved
    );
};

export const getSupervisorProjectsByStatuses = (
    projects: ProjectModel[],
    statuses: ProjectStatus[],
    userEmail: string | undefined
): ProjectModel[] => {
    if (!projects) {
        return new Array<ProjectModel>();
    }
    return projects.filter(
        ({ currentStatus, supervisor, secondSupervisorProposals }) =>
            (statuses.includes(currentStatus) &&
                supervisor.email === userEmail) ||
            secondSupervisorProposals?.some(
                (ssp) =>
                    ssp.status === ProjectSecondSupervisorStatus.accepted &&
                    ssp.supervisorUser.email === userEmail
            )
    );
};

export const getProposedProjects = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: Array<T>
): Array<T> => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) => currentStatus === ProjectStatus.pending
    );
};

export const getDeclinedSupervisorProjects = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: T[],
    userEmail: string | undefined
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({
            currentStatus,
            supervisor,
            secondSupervisorProposals,
            hasAdminDeclined,
        }) =>
            !hasAdminDeclined &&
            ((supervisor?.email === userEmail &&
                currentStatus === ProjectStatus.declined) ||
                secondSupervisorProposals?.some(
                    (ssp) =>
                        ssp.supervisorUser.email === userEmail &&
                        ssp?.status === ProjectSecondSupervisorStatus.declined
                ))
    );
};

export const getOverdueProjects = (
    projects: ProjectBrowserModel[]
): ProjectBrowserModel[] => {
    if (!projects) {
        return [];
    }

    return projects.filter((proj) => {
        const dateValue = getProjectDeadline(proj);
        if (!dateValue) {
            return false;
        }

        return isDateBeforeCheckDate(dateValue, new Date());
    });
};

export const getGradedProjects = <T extends ProjectModel | ProjectBrowserModel>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) => currentStatus === ProjectStatus.evaluated
    );
};

export const getActiveProjects = <T extends ProjectModel | ProjectBrowserModel>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    const activeProjectStatuses = [
        ProjectStatus.approved,
        ProjectStatus.registered,
        ProjectStatus.handedIn,
        ProjectStatus.evaluated,
    ];
    return projects.filter((project) =>
        activeProjectStatuses.includes(project.currentStatus)
    );
};

export const getSupervisorActiveProjects = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) =>
            currentStatus === ProjectStatus.approved ||
            currentStatus === ProjectStatus.registered ||
            currentStatus === ProjectStatus.handedIn
    );
};

export const getProposals = <T extends ProjectModel | ProjectBrowserModel>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) =>
            currentStatus === ProjectStatus.approved ||
            currentStatus === ProjectStatus.pending
    );
};

export const getHandedInProjects = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) => currentStatus === ProjectStatus.handedIn
    );
};

export const getWithdrawnProjects = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) => currentStatus === ProjectStatus.withdrawn
    );
};

export const getRegisteredProjects = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: T[]
): T[] => {
    if (!projects) {
        return new Array<T>();
    }

    return projects.filter(
        ({ currentStatus }) => currentStatus === ProjectStatus.registered
    );
};

export const getSortedProjectsForEvaluation = <
    T extends ProjectModel | ProjectBrowserModel
>(
    projects: T[]
): T | null => {
    if (!projects) {
        return null;
    }

    const projectsForEvaluation = projects.filter(
        ({ currentStatus }) => currentStatus === ProjectStatus.handedIn
    );

    return sortByEvaluationDate(projectsForEvaluation);
};

export const addDaysToDate = (date: Date, days: number | null): Date => {
    const newDate = new Date(date.toISOString());
    newDate.setDate(newDate.getDate() + Number(days));
    return newDate;
};

export const removeDaysFromDate = (date: Date, days: number | null): Date => {
    const newDate = new Date(date.toISOString());
    newDate.setDate(newDate.getDate() - Number(days));
    return newDate;
};

export const isOpenSupervision = (project: ProjectModel): boolean => {
    const activeProjectStatuses = [
        ProjectStatus.approved,
        ProjectStatus.registered,
        ProjectStatus.handedIn,
        ProjectStatus.evaluated,
        ProjectStatus.failed,
    ];

    return activeProjectStatuses.includes(project.currentStatus);
};

export const getAcceptedTeamMemberCount = (project: ProjectModel): number => {
    return project?.teamList
        ? project.teamList.filter(
              (member) => member?.invitationStatus === InvitationStatus.accepted
          ).length
        : 0;
};

export function getMySupervisionPendingProjects(
    projects: ProjectModel[],
    supervisor: User | null
): ProjectModel[] {
    if (!projects || !supervisor) {
        return new Array<ProjectModel>();
    }

    return projects.filter(
        (project) =>
            (project.supervisor?.email === supervisor.email &&
                project.currentStatus === ProjectStatus.pending) ||
            project.secondSupervisorProposals?.some(
                (ssp) =>
                    ssp.supervisorUser?.email === supervisor.email &&
                    ssp?.status === ProjectSecondSupervisorStatus.pending
            )
    );
}

export function getMySupervisionPendingProjectsIndexMap(
    projectsIndexMaps: Array<ProjectIndexMap>,
    supervisor: User | null
): Array<ProjectIndexMap> {
    if (!projectsIndexMaps || !supervisor) {
        return new Array<ProjectIndexMap>();
    }
    return projectsIndexMaps.filter(
        (projectsIndexMap) =>
            (projectsIndexMap.project.supervisor?.email === supervisor.email &&
                projectsIndexMap.project.currentStatus ===
                    ProjectStatus.pending) ||
            projectsIndexMap.project.secondSupervisorProposals?.some(
                (ssp) =>
                    ssp.supervisorUser?.email === supervisor.email &&
                    ssp?.status === ProjectSecondSupervisorStatus.pending
            )
    );
}

const sortByEvaluationDate = <T extends ProjectModel | ProjectBrowserModel>(
    projects: T[]
): T => {
    return (
        projects
            .slice()
            .sort((projectForCompare: T, nextProjectForCompare: T) => {
                if (
                    !projectForCompare.dates.deadlineDates?.evaluation ||
                    !nextProjectForCompare.dates.deadlineDates?.evaluation
                ) {
                    return 1;
                }
                return (
                    projectForCompare.dates.deadlineDates.evaluation.valueOf() -
                    nextProjectForCompare.dates.deadlineDates.evaluation.valueOf()
                );
            })[0] || null
    );
};

export const isFirstSupervisor = (
    project: ProjectModel | ProjectBrowserModel,
    currentUser: User | null
) => {
    return project.supervisor?.email === currentUser?.email;
};

export const isFirstSupervisorById = (
    project: ProjectModel | ProjectBrowserModel,
    supervisorId: string
) => {
    return project.supervisor?._id === supervisorId;
};

export const isSecondSupervisor = (
    project: ProjectModel | ProjectBrowserModel,
    currentUser: User | null
) => {
    return project.secondSupervisorProposals?.some(
        (ssp) => ssp?.supervisorUser?._id === currentUser?._id
    );
};

export const isSecondSupervisorById = (
    project: ProjectModel | ProjectBrowserModel,
    supervisorId: string
) => {
    return project.secondSupervisorProposals?.some(
        (ssp) => ssp.supervisorUser._id === supervisorId
    );
};

export const isAdditionalSupervisorById = (
    project: ProjectModel | ProjectBrowserModel,
    supervisorId: string
) => {
    return project.additionalSupervisor?._id === supervisorId;
};

export const isAcceptedSecondSupervisorById = (
    project: ProjectModel | ProjectBrowserModel,
    supervisorId: string
) => {
    return project.secondSupervisorProposals?.some(
        (ssp) =>
            ssp.supervisorUser?._id === supervisorId &&
            ssp.status === ProjectSecondSupervisorStatus.accepted
    );
};

export const hasAcceptedAsSecondSupervisor = (
    project: ProjectModel | ProjectBrowserModel,
    currentUser: User | null
): boolean => {
    return !!project.secondSupervisorProposals?.find(
        (ssp) =>
            ssp.supervisorUser._id === currentUser?._id &&
            ssp?.status === ProjectSecondSupervisorStatus.accepted
    );
};

export const isInvitedAsSecondSupervisor = (
    project: ProjectModel,
    currentUser: User | null
): boolean => {
    return !!project.secondSupervisorProposals?.find(
        (ssp) =>
            ssp.supervisorUser._id === currentUser?._id &&
            ssp?.status === ProjectSecondSupervisorStatus.pending
    );
};

export const countProjectsByStatus = (
    status: ProjectStatus,
    projects: Array<ProjectModel>
) => {
    return projects.reduce((result, project) => {
        if (project.currentStatus === status) {
            result++;
        }
        return result;
    }, 0);
};

export const mapProjectStateToDeadlineStatus = (
    status: ProjectStatus
): string => {
    let retVal = '';
    switch (status) {
        case ProjectStatus.pending:
        case ProjectStatus.declined:
        case ProjectStatus.approved:
        case ProjectStatus.saved: {
            retVal = DeadlineStatus.registration;
            break;
        }
        case ProjectStatus.registered: {
            retVal = DeadlineStatus.submission;
            break;
        }
        case ProjectStatus.handedIn: {
            retVal = DeadlineStatus.grading;
            break;
        }
        case ProjectStatus.evaluated:
        case ProjectStatus.failed: {
            retVal = DeadlineStatus.archiving;
            break;
        }
    }
    return retVal;
};

export const getLocalizedProjectDeadlineStatus = (
    deadlineText: string,
    localization: ILocalization
): string => {
    let retVal = '';
    switch (deadlineText) {
        case DeadlineStatus.registration: {
            retVal =
                localization.localizationStrings.susy.admin.projects.deadlines
                    .registration;
            break;
        }
        case DeadlineStatus.submission: {
            retVal =
                localization.localizationStrings.susy.admin.projects.deadlines
                    .submission;
            break;
        }
        case DeadlineStatus.grading: {
            retVal =
                localization.localizationStrings.susy.admin.projects.deadlines
                    .grading;
            break;
        }
        case DeadlineStatus.archiving: {
            retVal =
                localization.localizationStrings.susy.admin.projects.deadlines
                    .archiving;
            break;
        }
    }
    return retVal;
};

export const getProjectDeadline = (
    project: ProjectModel | ProjectBrowserModel
): Date | null => {
    const NO_DATE = null;
    let deadline: Date | null = NO_DATE;
    const deadlineStatus = mapProjectStateToDeadlineStatus(
        project.currentStatus
    );
    switch (deadlineStatus) {
        case DeadlineStatus.registration: {
            const registrationDeadline =
                project.projectTimeline?.registrationDeadlineDate;
            deadline = registrationDeadline ? registrationDeadline : NO_DATE;
            break;
        }
        case DeadlineStatus.submission: {
            deadline = project.dates.deadlineDates?.submission
                ? new Date(project.dates.deadlineDates.submission)
                : NO_DATE;
            break;
        }
        case DeadlineStatus.grading: {
            deadline = project.dates.deadlineDates?.evaluation
                ? new Date(project.dates.deadlineDates.evaluation)
                : NO_DATE;
            break;
        }
        case DeadlineStatus.archiving: {
            const date = project?.dates?.deadlineDates?.evaluation
                ? new Date(project?.dates?.deadlineDates?.evaluation)
                : null;
            deadline = date
                ? addDaysToDate(date, ARCHIVE_DURATION_IN_DAYS)
                : NO_DATE;
            break;
        }
    }
    return deadline;
};

export const getProjectDeadlineStr = (project: ProjectModel): string => {
    const deadline = getProjectDeadline(project);
    return deadline
        ? formatDateWithShortTime(deadline, DateFormat.MMM_D_COMMA_YYYY)
        : NO_DATA;
};

export const isWaitingForAdminProjectRegistrationApproval = (
    projectStatus: ProjectStatus | undefined,
    projectRegistration: IProjectRegistrationModel | undefined
): boolean => {
    let retVal = false;
    if (
        projectStatus === ProjectStatus.approved &&
        projectRegistration &&
        projectRegistration.isAdminAppovalRequired &&
        projectRegistration.requestStatus === AdminApprovalOutcomeEnum.PENDING
    ) {
        retVal = true;
    }

    return retVal;
};

export const isProjectProposalAttemptedAfterRegistrationDeadline = (
    projectTimeline: ProjectTimeline | undefined,
    projectStatus: ProjectStatus | undefined
): boolean => {
    let retVal = false;

    const now = new Date();
    if (
        projectStatus === ProjectStatus.pending &&
        projectTimeline?.registrationDeadlineDate &&
        now > projectTimeline?.registrationDeadlineDate
    ) {
        retVal = true;
    }

    return retVal;
};
